import { Divider, Flex, Space, Switch, Tag, Typography } from 'antd';
import { FC } from 'react';
import { ArrowRightShort } from 'react-bootstrap-icons';

import { DescriptionBlock, DomainPunycode } from '@/common/components';
import { CommonDrawerTitle } from '@/common/layouts';

import { DomainVisitors } from '@/modules/Domains';
import {
  ExternalRedirect,
  ExternalRedirectTargetType,
  getExternalRedirectSourceDomainLabel,
  getExternalRedirectSourceLabel,
  getExternalRedirectTargetDomainLabel,
  getExternalRedirectTargetDomainTagColor,
  getExternalRedirectTargetLabel,
} from '@/modules/ExternalRedirects';
import { getRedirectMatchModeLabel } from '@/modules/Sites';

import { ExternalRedirectsOverviewDropdown } from './ExternalRedirectsOverviewDropdown';

interface Props {
  externalRedirect: ExternalRedirect;
  disableButtons: boolean;
  isDeleteExternalRedirectLoading: boolean;
  isActivityExternalRedirectLoading: boolean;
  handleEditExternalRedirect: () => void;
  handleDeleteExternalRedirect: () => void;
  handleActivateExternalRedirect: () => void;
}

export const ExternalRedirectsOverviewView: FC<Props> = ({
  externalRedirect,
  disableButtons,
  isDeleteExternalRedirectLoading,
  isActivityExternalRedirectLoading,
  handleEditExternalRedirect,
  handleDeleteExternalRedirect,
  handleActivateExternalRedirect
}) => {
  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Space>
            <Switch
              checked={ externalRedirect.isActive }
              loading={ isActivityExternalRedirectLoading }
              disabled={ disableButtons }
              checkedChildren="Активно"
              unCheckedChildren="Вимкнено"
              onClick={ handleActivateExternalRedirect }
            />
            <Divider type="vertical"/>
            <ExternalRedirectsOverviewDropdown
              isItemsDisabled={ disableButtons }
              isDeleteExternalRedirectLoading={ isDeleteExternalRedirectLoading }
              handleEditExternalRedirect={ handleEditExternalRedirect }
              handleDeleteExternalRedirect={ handleDeleteExternalRedirect }
            />
          </Space>
        }
      />

      <DescriptionBlock
        items={ [[
          'Домени',
          <Flex key="domains" gap={ 8 } align="center" wrap>
            <Tag color="geekblue">
              <DomainPunycode domainName={ getExternalRedirectSourceDomainLabel(externalRedirect) } withIcon={ false }/>
            </Tag>
            <ArrowRightShort size={ 14 }/>
            <Tag color={ getExternalRedirectTargetDomainTagColor(externalRedirect.toType) }>
              { externalRedirect.toType === ExternalRedirectTargetType.Site
                ? `Сайт: ${ getExternalRedirectTargetDomainLabel(externalRedirect) || '—' }`
                : <DomainPunycode domainName={ getExternalRedirectTargetDomainLabel(externalRedirect) || '—' } withIcon={ false }/> }
            </Tag>
          </Flex>
        ]] }/>

      <DescriptionBlock items={ [['З', <Typography.Text key="source">{ getExternalRedirectSourceLabel(externalRedirect) }</Typography.Text>]] }/>

      <DescriptionBlock items={ [['На', <Typography.Text key="target">{ getExternalRedirectTargetLabel(externalRedirect) }</Typography.Text>]] }/>

      <DescriptionBlock items={ [['Код', <Tag key="statusCode" color="blue">{ externalRedirect.statusCode }</Tag>]] }/>

      <DescriptionBlock items={ [['Режим', <Tag key="matchMode" color="geekblue">{ getRedirectMatchModeLabel(externalRedirect.matchMode) }</Tag>]] }/>

      <DescriptionBlock
        items={ [[
          'Для відвідувача',
          <>
            { externalRedirect.forVisitor === DomainVisitors.GoogleBot && <Tag color="geekblue">Google Bot</Tag> }
            { externalRedirect.forVisitor === DomainVisitors.Any && <Tag color="geekblue">Будь-хто</Tag> }
            { externalRedirect.forVisitor === DomainVisitors.Person && <Tag color="geekblue">Людина</Tag> }
          </>
        ]] }/>
    </>
  );
};
